<script setup>
import {computed, ref, watch} from 'vue'
import {useItemDataStore} from '@/stores/itemData'
import {useViewStore} from '@/stores/view'

// Define Props
const props = defineProps({
    viewId: String,
    columnId: Number,
    column: Object,
})

// Dynamic View Store
const viewStore = useViewStore(props.viewId)

// Pinia Store
const itemDataStore = useItemDataStore()

// Local Reactive State
const structure = ref(undefined)

// Computed Properties
const orderBy = computed(() => viewStore.orderBy)
const orderDirection = computed(() => viewStore.orderDirection)

// Native watcher replacing useAsyncState for structure fetching
watch(
    () => props.columnId,
    async (newColumnId) => {
        if (!newColumnId) {
            structure.value = undefined
            return
        }

        structure.value = await itemDataStore.get({
            tableName: 'modulestructure',
            id: newColumnId
        })
    },
    {immediate: true}
)

const typeClass = computed(() => {
    return type.value ? 'table-cell-type-' + type.value : ''
})

const sortedAsc = computed(() => {
    return (
        orderBy.value === columnName.value &&
        orderDirection.value === 'Asc'
    )
})

const sortedDesc = computed(() => {
    return (
        orderBy.value === columnName.value &&
        orderDirection.value === 'Desc'
    )
})

const title = computed(() => {
    if (props.column) {
        return props.column.title
    } else if (structure.value) {
        return structure.value.title
    }
    return ''
})

const columnName = computed(() => {
    if (props.column) {
        return props.column.name
    } else if (structure.value) {
        return structure.value.columnName
    }
    return undefined
})

const type = computed(() => {
    if (structure.value) {
        return structure.value.type
    }
    return undefined
})

const isClickable = computed(() => {
    return type.value !== 'relationshipManyToMany'
})

// Methods
const onClick = () => {
    // The Relationship-Many-to-Many column is not sortable.
    if (!isClickable.value) {
        return
    }

    let direction = (
        orderBy.value === columnName.value &&
        orderDirection.value === 'Asc'
    )
        ? 'Desc'
        : 'Asc'

    viewStore.setOrderBy(columnName.value)
    viewStore.setOrderDirection(direction)
}
</script>

<template>
    <th :class="[typeClass, {'disabled': !isClickable}]" role="button" @click="onClick">
        <span>{{ title }}</span>
        <span :class="{ 'bi-chevron-up': sortedAsc, 'bi-chevron-down': sortedDesc }" class="text-danger"/>
    </th>
</template>

<style scoped>
.disabled {
    cursor: not-allowed !important;
}

/* Tone down the default colour so that it becomes brighter on hover */
[data-bs-theme="light"] th:not(:hover) {
    color: #666
}

[data-bs-theme="dark"] th:not(:hover) {
    color: #999
}
</style>